import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import styles from './Modal.module.css';
import { createPago } from '../../../../../services/pagoService';
import { Pago } from '../../../../../interfaces/pago.types';

interface AddPagoModalProps {
    onClose: () => void;
    onPagoAdded: (newPago: Pago) => void;
    caseId: number;
}

export const AddPagoModal: React.FC<AddPagoModalProps> = ({ onClose, onPagoAdded, caseId }) => {
    const [montoPagado, setMontoPagado] = useState<string>('');
    const [fechaPago, setFechaPago] = useState<string>(new Date().toISOString().split('T')[0]);
    const [descripcion, setDescripcion] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!montoPagado || !fechaPago) {
            setError('El monto y la fecha son obligatorios.');
            return;
        }
        if (parseFloat(montoPagado) <= 0) {
            setError('El monto debe ser un valor positivo.');
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const pagoData = {
                caso_id: caseId,
                monto_pagado: parseFloat(montoPagado),
                fecha_pago: fechaPago,
                descripcion: descripcion
            };
            const newPago = await createPago(pagoData);
            onPagoAdded(newPago);
            onClose(); 
        } catch (err: any) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };
    
    const modalVariants = {
        hidden: { y: 50, opacity: 0 },
        visible: { y: 0, opacity: 1 },
        exit: { y: 50, opacity: 0 }
    };

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <motion.div 
                className={styles.modalContent}
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                onClick={(e) => e.stopPropagation()}
            >
                <header className={styles.modalHeader}>
                    <h2>Registrar Nuevo Pago</h2>
                    <button onClick={onClose} className={styles.closeButton}><X size={24} /></button>
                </header>
                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.formGrid}>
                        <div className={styles.formGroup}>
                            <label htmlFor="monto_pagado">Monto Pagado (S/)*</label>
                            <input 
                                type="number" 
                                id="monto_pagado" 
                                name="monto_pagado" 
                                value={montoPagado}
                                onChange={(e) => setMontoPagado(e.target.value)}
                                placeholder="0.00"
                                step="0.01"
                                required 
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="fecha_pago">Fecha del Pago*</label>
                            <input 
                                type="date" 
                                id="fecha_pago" 
                                name="fecha_pago" 
                                value={fechaPago}
                                onChange={(e) => setFechaPago(e.target.value)} 
                                required 
                            />
                        </div>
                        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                            <label htmlFor="descripcion">Descripción (Opcional)</label>
                            <input 
                                type="text" 
                                id="descripcion" 
                                name="descripcion" 
                                value={descripcion}
                                onChange={(e) => setDescripcion(e.target.value)}
                                placeholder="Ej: Adelanto de honorarios, Cuota #2"
                            />
                        </div>
                    </div>
                    {error && <p className={styles.errorMessage}>{error}</p>}
                    <footer className={styles.modalFooter}>
                        <button type="button" className={styles.cancelButton} onClick={onClose} disabled={isLoading}>
                            Cancelar
                        </button>
                        <button type="submit" className={styles.submitButton} disabled={isLoading}>
                            {isLoading ? 'Guardando...' : 'Registrar Pago'}
                        </button>
                    </footer>
                </form>
            </motion.div>
        </div>
    );
};